<script setup>

import SearchToolbar from "@/components/shipments/SearchToolbar";
import GridModeDataNotification from "@/components/shipments/GridModeDataNotification";
import GridCellDetail from "@/components/common/other/GridCellDetail";
import StickyGrid from "@/components/common/other/StickyGrid";
import Button from "@/components/common/input/Button";
import CheckBox from "@/components/common/input/CheckBox";
import Tabs from "@/components/common/navigation/tabs/Tabs";
import TabsContent from "@/components/common/navigation/tabs/TabsContent";
import StaticTooltip from "@/components/common/other/StaticTooltip"
import LoadingSpinner from "@/components/common/other/LoadingSpinner";
import Notification from "@/classes/component-classes/Notification";
import ShipmentSearchColumns from "@/classes/global/ShipmentSearchColumns";
import NoDataFoundBanner from "@/components/shipments/NoDataFoundBanner";
import DateInformationTooltip from "@/components/common/other/DateInformationTooltip";
import BusySpinner from "@/components/common/other/BusySpinner";

// Vue setup script imports
import {inject, onMounted, ref, computed, watch, reactive, toRefs} from 'vue'
import {useRouter} from 'vue-router';

// Class Imports
import Validation from "@/classes/global/Validation";

// Usings
import {useStore} from 'vuex';
import {useRoute} from "vue-router";
import {useDownloadLocalFile} from "@/composables/common/useDownloadLocalFile";
import {useGetDocumentsList} from "@/composables/common/useGetDocumentsList";
import API from "@/classes/global/API";
import ColumnSort from "@/classes/global/ColumnSort";
import PreJobAirBookingValidation from "@/classes/bookings/PreJobAirBookingValidation";
import PreJobSeaBookingValidation from "@/classes/bookings/PreJobSeaBookingValidation";
import PreJobRoadBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";
import PreJobRailBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";

// Constants
const validationViewModel = inject('validationViewModel');
const shipments = inject("shipments");

const store = useStore();
const route = useRoute();
const router = useRouter();

const searchValidation = ref(new Validation('3', false));
const refreshGrids = ref({air: false, road: false, sea: false, rail: false, courier: false, other: false});

const shipmentAllFilter = computed(() => {

  if(store.state.userSettings.showShipmentJobs === true || store.state.userSettings.showShipmentBookings === true) {

    return true;
  }
})

const props = defineProps({

  modelValue: {}
})


// Hard time getting this to work any other way, to keep reacive props for the tab dataBadge. Needs the computed!!
const airData = reactive({air: null, road: null, sea: null, rail: null, courier: null, other: null});
const {air, road, sea, rail, courier, other} = toRefs(airData)

const getAirLength = computed(() => {
  return air
});
const getRoadLength = computed(() => {
  return road
});
const getSeaLength = computed(() => {
  return sea
});
const getRailLength = computed(() => {
  return rail
});
const getCourierLength = computed(() => {
  return courier
});
const getOtherLength = computed(() => {
  return other
});

const modes = ['air', 'road', 'sea', 'rail', 'courier', 'other'];

let tabRenderSeed = ref(0);

let isConsolView = false;

onMounted(() => {

  shipments.value.showShipmentsHideCompleted = JSON.parse(localStorage.getItem('showShipmentsHideCompleted'));

  const savedAutoRefresh = localStorage.getItem('autoRefreshShipments');
  if (savedAutoRefresh === 'true') {
    shipments.value.autoRefreshShipments = true;
    autoRefreshGrid(); // Start the interval if it was previously enabled
  }

  if(shipments.value.isConsolJobView && Object.entries(shipments.value.currentConsolJob).length > 0 &&
      Object.entries(props.modelValue).length === 0) {

    // Reload the consol job when coming back into shipments
    return mapShipmentData(shipments.value.currentConsolJob);
  }

  if(Object.entries(props.modelValue).length !== 0) {

    shipments.value.isConsolJobView = true;
    shipments.value.currentConsolJob = props.modelValue;

    // This maps the grid with any selected single consol job sent in through modelValue
    mapShipmentData(props.modelValue);

    return;
  }

  if(!shipments.value.selectedShowShipmentsTab) {

    shipments.value.selectedShowShipmentsTab = store.state.userSettings.showShipmentsTabSettings.default;

    // Set a place for holding the value for reseting radio button to current default if a tab is turned off when user
    // is using the tab config menu
    shipments.value.currentDefaultTab = shipments.value.selectedShowShipmentsTab;

  }

  if(shipments.value.shipmentsSearchTerm) {
    searchShipments();

  } else {

    shipments.value.showingShipmentSearchData = false;
    configureFilters();

  }

  let columnSorted = getSortedColumn();

  if(columnSorted) {

    prepareResetColumnSorting();
    handleColumnSort(columnSorted);

  } else {

    resetAllGridSorting();

  }

  if (savedAutoRefresh === 'true') {
    shipments.value.autoRefreshShipments = true;
    autoRefreshGrid(); // Start the interval if it was previously enabled
  }

})

function getSortedColumn() {

  // Keep state of any grid column that has been sorted per current mode only
  let columnSorted = null;

  store.state.userSettings.searchShipmentColumns[shipments.value.selectedShowShipmentsTab.toLowerCase()].forEach(column => {

    if(column.sortDescending !== null) {

      columnSorted = column;

    }
  })

  return columnSorted;
}

function prepareResetColumnSorting() {

  modes.forEach(mode => {

    if(mode !== shipments.value.selectedShowShipmentsTab.toLowerCase()) {

      resetGridColumnSorting(mode);
    }
  })

}

function resetGridColumnSorting(mode) {

  store.state.userSettings.searchShipmentColumns[mode].forEach(column => {

    column.sortDescending = null;
  })

}

function resetAllGridSorting() {

  modes.forEach(mode => {

      resetGridColumnSorting(mode);

  })

}

function getCorrectDate(date) {

  if (date.actual === '') return date.est
  return date.actual
}

function saveGridColumns(colWidth) {

  let searchShipmentColumns = new ShipmentSearchColumns();

  searchShipmentColumns.securityId = store.state.userSettings.userId;
  searchShipmentColumns.mode = shipments.value.selectedShowShipmentsTab;
  searchShipmentColumns.columns = store.state.userSettings.searchShipmentColumns[shipments.value.selectedShowShipmentsTab.toLowerCase()];

  const response = API.saveGridColumns(searchShipmentColumns);

  if (response) {

    store.state.hideLoadingSpinner = true;
    //prevent save banner from showing after column size change
    if(colWidth !== true)
    store.state.banners.success = new Notification(true, 'Column Configuration Saved Successfully')
  }

}

function resetGridColumns() {

  const response = API.resetGridColumns(shipments.value.selectedShowShipmentsTab);

  if (response) {

    store.state.hideLoadingSpinner = true;
    store.state.banners.success = new Notification(true, 'Columns Reset Successfully')
  }

}

function handleSearchFiltering() {

  if (shipments.value.shipmentsSearchTerm) searchShipments();

  refreshData();

}

function prepareSearch() {

  if (shipments.value.shipmentsSearchTerm) searchShipments();
  if (!shipments.value.shipmentsSearchTerm) searchValidation.value.value = true;
}

function clearValidation() {

  searchValidation.value.value = false;
}

function refreshData(clearedSearch) {

  shipments.value.isConsolJobView = false;
  resetAllGridSorting();

  if(clearedSearch === true) {

    shipments.value.showingShipmentSearchData = false;

    getShipmentsData();

    return
  }

  if (shipments.value.shipmentsSearchTerm) searchShipments();

  // Logic to ensure pagination does not reset to page 1 during refresh of searched data
  // Only reset if the search term is cleared going back to 90 days of data.
  // We know if the showingShipmentSearchData flag is true at this point, it must have been a search.
  // This prevents the pagination refresh when not using search.
  if (!shipments.value.shipmentsSearchTerm && shipments.value.showingShipmentSearchData === true) {

    shipments.value.showingShipmentSearchData = false;

    resetGridPagination();

    getShipmentsData();

  } else if (!shipments.value.shipmentsSearchTerm) {

    getShipmentsData()
  }
}

function refreshGridData() {

  for(let each in refreshGrids.value) {

    refreshGrids.value[each] = !refreshGrids.value[each]
  }

}

async function searchShipments() {

  if(shipments.value.selectedShowShipmentsTab !== '+/-') {

    const selectedMode = shipments.value.selectedShowShipmentsTab.toLowerCase();

    // Get current data set length to use for logic when maintianing state for pagination (Reset to page 1 if is new search)
    shipments.value.currentModeDataLength = shipments.value.shipmentsData[selectedMode].length;

    if(shipments.value.shipmentsSearchTerm) {

      searchValidation.value.value = false;
      shipments.value.showingShipmentSearchData = true;
      store.state.hideLoadingSpinner = false;

      const response = await API.searchShipments(shipments.value.shipmentsSearchTerm,
          route, shipments.value.showShipmentsDataFilterType, shipments.value.showShipmentsHideCompleted);

      if (response) {

        store.state.hideLoadingSpinner = true;
        refreshGridData();

        try {

          if (response[selectedMode].data.length !== shipments.value.currentModeDataLength) resetGridPagination();

          mapShipmentData(response);

          prepareResetColumnSorting();

          let columnSorted = getSortedColumn();

          if(columnSorted) {

            // prepareResetColumnSorting();
            handleColumnSort(columnSorted);
          }



        } catch (error) {

          // TODO Log Error
        }

      }
    }

  }

}

function resetGridPagination() {

  for (let page in shipments.value.shipmentGridCurrentPage) {
    shipments.value.shipmentGridCurrentPage[page] = 1
  };

  for (let page in shipments.value.shipmentGridCurrentPaginationPage) {
    shipments.value.shipmentGridCurrentPaginationPage[page] = 1
  }

}

async function getShipmentsData() {

  if (shipments.value.showingShipmentSearchData === false) {

    store.state.hideLoadingSpinner = false;

    const response = await API.getShipments(route,
        shipments.value.showShipmentsDataFilterType,
        shipments.value.showShipmentsHideCompleted, store.state.userSettings.shipmentTimeFilter);

    if (response) {

      store.state.hideLoadingSpinner = true;
      refreshGridData();

      mapShipmentData(response);

    }

  }

}

function configureFilters() {

  if(store.state.userSettings.showShipmentBookings) shipments.value.showShipmentsDataFilterType = 'Bookings';

  if(store.state.userSettings.showShipmentJobs) shipments.value.showShipmentsDataFilterType = 'Jobs';

  if(store.state.userSettings.showShipmentBookings && store.state.userSettings.showShipmentJobs) {

    shipments.value.showShipmentsDataFilterType = 'All';
  }

  getShipmentsData();
}

async function downloadToExcel() {

  shipments.value.hideExcelDownloadBusy = false;

  const response = await API.getShipmentsExcelFile(shipments.value.shipmentsSearchTerm,
      shipments.value.showShipmentsDataFilterType, shipments.value.showShipmentsHideCompleted);

  if (response) {

    useDownloadLocalFile(response);

  }

  shipments.value.hideExcelDownloadBusy = true;
}

function mapShipmentData(response) {

  shipments.value.shipmentsData.air = response.air.data;
  airData.air = response.air.data.length;
  shipments.value.shipmentsData.road = response.road.data;
  airData.road = response.road.data.length;
  shipments.value.shipmentsData.sea = response.sea.data;
  airData.sea = response.sea.data.length;
  shipments.value.shipmentsData.rail = response.rail.data;
  airData.rail = response.rail.data.length;
  shipments.value.shipmentsData.courier = response.courier.data;
  airData.courier = response.courier.data.length;
  shipments.value.shipmentsData.other = response.other.data;
  airData.other = response.other.data.length;

  let columnSorted = getSortedColumn();

  if(columnSorted) {

    prepareResetColumnSorting();
    handleColumnSort(columnSorted);

  } else {

    resetAllGridSorting();

  }
}

function getBookingType(row) {

  if (shipments.value.selectedShowShipmentsTab === 'Other' || shipments.value.selectedShowShipmentsTab === 'Courier') {

    let section;

    if (shipments.value.selectedShowShipmentsTab === 'Other') section = 'freightBooking';
    if (shipments.value.selectedShowShipmentsTab === 'Courier') section = 'courierBooking';

    router.push({name: 'booking', params: {owner: store.state.owner, section: section, id: row.grid.id}});

  } else {

    router.push({
      name: 'prejob',
      params: {owner: store.state.owner, section: shipments.value.selectedShowShipmentsTab, id: row.grid.id}
    });
  }

}

function handleTrackingLink(url) {

  window.open(url);
}


function handleTabSelection(tab) {

  if (tab === store.state.userSettings.showShipmentsTabSettings.default) {

    shipments.value.activeShipmentTabs[tab.toLowerCase()].active =
        !shipments.value.activeShipmentTabs[tab.toLowerCase()].active

  }

}

function handleRadioButton(button) {
  if(!shipments.value.activeShipmentTabs[button.toLowerCase()].active) {

    store.state.userSettings.showShipmentsTabSettings.default = shipments.value.currentDefaultTab;
  }

}

function handleTabConfigSave() {

  store.state.userSettings.showShipmentsTabSettings.air = shipments.value.activeShipmentTabs.air.active;
  store.state.userSettings.showShipmentsTabSettings.road = shipments.value.activeShipmentTabs.road.active;
  store.state.userSettings.showShipmentsTabSettings.sea = shipments.value.activeShipmentTabs.sea.active;
  store.state.userSettings.showShipmentsTabSettings.rail = shipments.value.activeShipmentTabs.rail.active;
  store.state.userSettings.showShipmentsTabSettings.courier = shipments.value.activeShipmentTabs.courier.active;
  store.state.userSettings.showShipmentsTabSettings.other = shipments.value.activeShipmentTabs.other.active;

  const saveObject = {

    securityId: store.state.userSettings.userId,
    showShipmentsTabSettings: store.state.userSettings.showShipmentsTabSettings

  }

  const response = API.saveShowShipmentsTabConfiguration(saveObject);

  if (response) {

    shipments.value.currentDefaultTab = store.state.userSettings.showShipmentsTabSettings.default
    tabRenderSeed.value ++;
    store.state.banners.success = new Notification(true, 'Tab Config Saved Successfully');

  }

}

function viewSelectedShipment(rowId) {

  store.state.hideLoadingSpinner = false;

  try {

    router.push({ name: 'viewshipment', params: { owner: store.state.owner, mode: shipments.value.selectedShowShipmentsTab, id: rowId }})

  } catch (error) {

    store.state.hideLoadingSpinner = true;
  }

}

async function handleColumnSort(col) {

  if(!shipments.value.isConsolJobView) {

    let columnSortData = new ColumnSort();

    columnSortData.typeFilter = shipments.value.showShipmentsDataFilterType;
    columnSortData.hideCompleted = shipments.value.showShipmentsHideCompleted;
    columnSortData.mode = shipments.value.selectedShowShipmentsTab.toLowerCase();
    columnSortData.searchTerm = shipments.value.shipmentsSearchTerm;
    columnSortData.userSecurityId = store.state.userSettings.userId;
    columnSortData.accountNumber =  store.state.userSettings.account;
    columnSortData.owner = store.state.owner;
    columnSortData.sortColumn = col;
    columnSortData.gridKey = 'showshipments'

    store.state.hideLoadingSpinner = false;

    let response = await API.sortShipmentsGridColumn(columnSortData);

    if(response.data.length > 0) {

      shipments.value.shipmentsData[columnSortData.mode] = response.data;

      store.state.hideLoadingSpinner = true;

    } else {

      store.state.hideLoadingSpinner = true;
    }

  }

}

async function viewConsolJobs(rowData) {

  shipments.value.currentSelectedConsolJob = rowData;
  store.state.hideLoadingSpinner = false;
  shipments.value.consolGridData = [];

  let consolJobs = await API.getShipmentConsolJobs(rowData.id);


  if (consolJobs && consolJobs.length > 0) {

    for (const item of consolJobs) {

      let mainDataSet = await API.searchShipments(item.jobNumber, route, shipments.value.showShipmentsDataFilterType);

      try {

        let shipmentData = mainDataSet[item.mode.toLowerCase()].data[0];

        shipmentData.mode = item.mode;
        shipments.value.consolGridData.push(shipmentData);

        shipments.value.openConsolDrawer = true;

      }catch(error) {

        store.state.banners.error = new Notification(true, 'Job Data Not Found, possible Mode missing');
        console.log(error);
      }

    }
  } else {

    store.state.banners.error = new Notification(true, `Consol Jobs Not Found For Job Id:- ${rowData.id}`);
    store.state.banners.error = new Notification(true, `Consol Jobs Not Found For Job Id:- ${rowData.id}`);
  }

  shipments.value.openConsolDrawer = true;

  store.state.hideLoadingSpinner = true;
}

function setHideCompletedJobs() {

  localStorage.setItem('showShipmentsHideCompleted', shipments.value.showShipmentsHideCompleted);

  handleSearchFiltering();
}

function autoRefreshGrid(){
  if(shipments.value.autoRefreshShipments){
    localStorage.setItem('autoRefreshShipments', 'true');

    shipments.value.autoRefreshInterval = setInterval(() => {

      if(shipments.value.isConsolJobView === true) return;

      shipments.value.shipmentsSearchTerm ? refreshData(false) : refreshData(true)
    }, 300000); //300000 = 5 minutes -->> for testing set to 2000 ms (2 seconds)

  } else {
    localStorage.setItem('autoRefreshShipments', 'false');

    clearInterval(shipments.value.autoRefreshInterval);
  } 
}

</script>

<template class="cmp-shipments">
  <LoadingSpinner :hide="store.state.hideLoadingSpinner"></LoadingSpinner>
  <SearchToolbar>
    <div class="cmp-shipments_toolbar">
      <StaticTooltip>
        <template #content>
          <strong>Searches Will Include The Following Criteria For All Modes!</strong>
          <div style="padding-top: 6px;">AWB Number, House Number, Bill Of Lading (BOL)</div>
          <div style="padding-top: 4px;">Container Number, Job Reference, Customer Reference</div>
        </template>
        <div class="global-info-icon"></div>
      </StaticTooltip>
      <TextInput
          data-cy="txtSearchShipment"
          :width="280"
          ref="searchInputRef"
          placeholder="Search..."
          :clearable="true"
          :validation-text="validationViewModel.validationMessages"
          :validation-fail="searchValidation"
          @keydown.enter="searchShipments()"
          @cleared="refreshData(true)"
          @focus="clearValidation"
          v-model="shipments.shipmentsSearchTerm"
          label="Search Shipments">
      </TextInput>
      <div
          title="Search"
          class="global-search-icon"
          style="margin: 0 40px 0 10px;"
          @mouseout="clearValidation"
          @focusout="clearValidation"
          @click="prepareSearch">&#129138;
      </div>
<!--      <Button-->
<!--          @click="refreshData"-->
<!--          text="Refresh"-->
<!--          type="secondary"-->
<!--          style="margin-right: 8px;">-->
<!--      </Button>-->
<!--      <StaticTooltip>-->
<!--        <template #content>-->
<!--          <span><strong>Refresh</strong> Retrieves The Latest Data & Clears Any Sorted Columns</span>-->
<!--          <div style="padding: 4px 0 0 14px;">Including Any Search Results</div>-->
<!--        </template>-->
<!--        <div class="global-info-icon"></div>-->
<!--      </StaticTooltip>-->
    </div>
    <div class="cmp-shipments_filter-section">
          <div class="cmp-shipments_filter-by-text">Filter Data By:</div>
      <input type="radio" name="shipmentfilters" id="All" value="All"
             :disabled="shipmentAllFilter" @change="handleSearchFiltering()"
             v-model="shipments.showShipmentsDataFilterType" title="Includes Bookings & Jobs"/>
      <label for="All">All</label>
      <input type="radio" name="shipmentfilters" id="Bookings" value="Bookings"
             :disabled="store.state.userSettings.showShipmentJobs" @change="handleSearchFiltering()"
             v-model="shipments.showShipmentsDataFilterType" title="Show Bookings Only"/>
      <label for="Bookings">Bookings</label>
      <input type="radio" name="shipmentfilters" id="Jobs" value="Jobs"
             :disabled="store.state.userSettings.showShipmentBookings" @change="handleSearchFiltering()"
             v-model="shipments.showShipmentsDataFilterType" title="Show All Active Jobs Only"/>
      <label for="Jobs">Jobs</label>
      <div class="cmp-shipments_hide-completed-chkbox">
        <div class="cmp-shipments_hide-completed" v-if="shipments.showShipmentsDataFilterType === 'Jobs'">
          <CheckBox
              @change="setHideCompletedJobs"
              :label-width="90"
              v-model="shipments.showShipmentsHideCompleted"
              label="Hide Completed">
          </CheckBox>
        </div>
      </div>
    </div>
    <div class="cmp-shipments_data-search-message">
      <span v-if="!shipments.showingShipmentSearchData"> Showing The Last <strong
          style="color: #168156">{{ store.state.userSettings.shipmentTimeFilter }}  Days Data</strong> For: </span>
      <span v-else> Showing <strong style="color: #168156">All Data</strong> For: </span>
      <span v-if="shipments.selectedShowShipmentsTab !== '+/-'"
            style="font-weight: bold"> {{ shipments.selectedShowShipmentsTab }}</span>
    </div>
    <div style="width: 150px">
      <BusySpinner
          v-if="!shipments.hideExcelDownloadBusy"
          :hide="shipments.hideExcelDownloadBusy"
          message="Downloading Data...">
      </BusySpinner>
      <Button
          v-if="shipments.hideExcelDownloadBusy"
          data-cy="btnExcelDownload"
          @click="downloadToExcel"
          text="Export"
          title="Export Data For All Modes"
          icon='<i class="fa-solid fa-file-excel"></i>'
          type="success"
          style="margin-right: 0px;">
      </Button>
    </div>
  </SearchToolbar>
  <div class="cmp-shipments_refresh-grid">
    <Button
        @click="refreshData"
        text="Refresh"
        type="toolbar"
        style="margin-right: 8px;">
    </Button>
      <CheckBox
          @change="autoRefreshGrid"
          :label-width="70"
          v-model="shipments.autoRefreshShipments"
          label="Auto Refresh">
      </CheckBox>
    <StaticTooltip :icon="false">
      <template #content>
        <span><strong>Refresh</strong> Retrieves The Latest Data & Clears Any Sorted Columns</span>
        <div style="padding: 4px 0 10px 0;">Including Any Search Results</div>
        <span><strong>Auto Refresh</strong> Will refresh the data every 5 mins</span>
      </template>
      <div class="global-info-icon" style="margin-left: 4px"></div>
    </StaticTooltip>
  </div>
  <Tabs v-model:selectedTab="shipments.selectedShowShipmentsTab" :key="tabRenderSeed">
    <TabsContent
        v-if="store.state.userSettings.showShipmentsTabSettings.air"
        :icon="require(`@/assets/icons/mode_air.png`)"
        :name="shipments.modes.air"
        :badgeData="getAirLength"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <StickyGrid
          v-if="shipments.shipmentsData.air.length > 0"
          :key="refreshGrids.air"
          :height="580"
          :page-size="200"
          @click-save="saveGridColumns"
          @click-reset="resetGridColumns"
          @sort-column="handleColumnSort"
          v-model:currentPage="shipments.shipmentGridCurrentPage.air"
          v-model:currentPaginationRange="shipments.shipmentGridCurrentPaginationPage.air"
          :selectable="false"
          :allowGridMenu="store.state.userSettings.allowColumnMenuSearchShipments"
          :allow-column-sort="true"
          :allow-column-resize="true"
          :row-selection="false"
          :columns="store.state.userSettings.searchShipmentColumns.air"
          :dataSource="shipments.shipmentsData.air">
        <template #right-content>
          <GridModeDataNotification
              style="margin-left: auto ">
          </GridModeDataNotification>
        </template>
        <template v-slot:selectRow="row">
          <Button
              v-if="row.grid.consol == -1"
              style="width:10px"
              title="View This Job"
              data-cy="btnViewShipment"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="V">
          </Button>
          <Button
              v-if="row.grid.consol== -1"
              style="width:10px; margin-left: 7px"
              title="Master Job / View Related Jobs"
              data-cy="btnViewShipment"
              @click="viewConsolJobs(row.grid)"
              text="M">
          </Button>
          <Button
              v-else
              data-cy="btnViewShipment"
              title="View This Job"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="View">
          </Button>
        </template>
        <template v-slot:editBooking="row">
          <Button
              :disabled="!row.grid.isEditable"
              data-cy="btnViewShipment"
              @click="getBookingType(row)"
              type="info"
              text="Edit">
          </Button>
        </template>
        <template v-slot:trackJob="row">
          <Button
              v-if="store.state.userSettings.showAirTrackingLink"
              :disabled="!row.grid.trackingUrl"
              data-cy="btnTrackShipment"
              icon="<i class='fa-duotone fa-arrow-up-right-from-square'></i>"
              @click="handleTrackingLink(row.rowData.trackingUrl)"
              type="secondary"
              text="TRACK">
          </Button>
        </template>
        <template v-slot:documents="row">
          <div v-if="row.grid.documents"
               title="View Documents"
               @click="useGetDocumentsList(row.grid.id, route, store, shipments)"
               class="fa-solid fa-folder-open grid-docs-icon">&#128194
          </div>
<!--          <i v-if="row.grid.documents"-->
<!--             title="View Documents"-->
<!--             data-cy="btnViewDocuments"-->
<!--             @click="useGetDocumentsList(row.grid.id, route, store, shipments)"-->
<!--             class="fa-solid fa-folder-open grid-docs-icon">-->
<!--          </i>-->
        </template>
        <template v-slot:pickupDate="row">
          <GridCellDetail
              v-if="row.grid.pickupDate.est || row.grid.pickupDate.actual"
              v-model:cellColor="row.grid.pickupDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.pickupDate.est"
                  :estTime="row.grid.pickupDate.estimatedTime"
                  :actualDate="row.grid.pickupDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.pickupDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:arrivalDate="row">
          <GridCellDetail
              v-if="row.grid.arrivalDate.est || row.grid.arrivalDate.actual"
              v-model:cellColor="row.grid.arrivalDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.arrivalDate.est"
                  :estTime="row.grid.arrivalDate.estimatedTime"
                  :actualDate="row.grid.arrivalDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.arrivalDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:departureDate="row">
          <GridCellDetail
              v-if="row.grid.departureDate.est || row.grid.departureDate.actual"
              v-model:cellColor="row.grid.departureDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.departureDate.est"
                  :estTime="row.grid.departureDate.estimatedTime"
                  :actualDate="row.grid.departureDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.departureDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:deliveryDate="row">
          <GridCellDetail
              v-if="row.grid.deliveryDate.est || row.grid.deliveryDate.actual"
              v-model:cellColor="row.grid.deliveryDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.deliveryDate.est"
                  :estTime="row.grid.deliveryDate.estimatedTime"
                  :actualDate="row.grid.deliveryDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.deliveryDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:customs="row">
          <GridCellDetail
              v-if="row.grid.customs.est || row.grid.customs.actual"
              v-model:cellColor="row.grid.customs.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.customs.est"
                  :estTime="row.grid.customs.estimatedTime"
                  :actualDate="row.grid.customs.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.customs) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:cleared="row">
          <GridCellDetail
              v-if="row.grid.cleared.est || row.grid.cleared.actual"
              v-model:cellColor="row.grid.cleared.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.cleared.est"
                  :estTime="row.grid.cleared.estimatedTime"
                  :actualDate="row.grid.cleared.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.cleared) }}</template>
          </GridCellDetail>
        </template>
      </StickyGrid>
      <NoDataFoundBanner v-else>
        <div style="display: flex; align-items: center; gap:20px;">
        <img src="@/assets/icons/mode_air.png">No Data Found For Air
      </div>
      </NoDataFoundBanner>
    </TabsContent>
    <TabsContent
        v-if="store.state.userSettings.showShipmentsTabSettings.road"
        :icon="require(`@/assets/icons/mode_road.png`)"
        :name="shipments.modes.road"
        :badgeData="getRoadLength"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <StickyGrid
          v-if="shipments.shipmentsData.road.length > 0"
          :key="refreshGrids.road"
          :height="580"
          :page-size="200"
          @click-save="saveGridColumns"
          @click-reset="resetGridColumns"
          @sort-column="handleColumnSort"
          v-model:currentPage="shipments.shipmentGridCurrentPage.road"
          v-model:currentPaginationRange="shipments.shipmentGridCurrentPaginationPage.road"
          :allowGridMenu="store.state.userSettings.allowColumnMenuSearchShipments"
          :allow-column-sort="true"
          :allow-column-resize="true"
          :selectable="false"
          :row-selection="false"
          :columns="store.state.userSettings.searchShipmentColumns.road"
          :dataSource="shipments.shipmentsData.road">
        <template #right-content>
          <GridModeDataNotification
              style="margin-left: auto">
          </GridModeDataNotification>
        </template>
        <template v-slot:selectRow="row">
          <Button
              v-if="row.grid.consol == -1"
              style="width:10px"
              title="View This Job"
              data-cy="btnViewShipment"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="V">
          </Button>
          <Button
              v-if="row.grid.consol== -1"
              style="width:10px; margin-left: 7px"
              title="Master Job / View Related Jobs"
              data-cy="btnViewShipment"
              @click="viewConsolJobs(row.grid)"
              text="M">
          </Button>
          <Button
              v-else
              data-cy="btnViewShipment"
              title="View This Job"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="View">
          </Button>
        </template>
        <template v-slot:editBooking="row">
<!--          <i class="fa-duotone fa-pen-to-square" style=" font-size: 20px; color: #359191; margin-left: 30px; cursor: pointer"></i>-->
          <Button
              :disabled="!row.grid.isEditable"
              data-cy="btnViewShipment"
              @click="getBookingType(row)"
              type="info"
              text="Edit">
          </Button>
        </template>
        <template v-slot:documents="row">
          <div v-if="row.grid.documents"
               title="View Documents"
               @click="useGetDocumentsList(row.grid.id, route, store, shipments)"
               class="fa-solid fa-folder-open grid-docs-icon">&#128194
          </div>
<!--          <i v-if="row.grid.documents"-->
<!--             title="View Documents"-->
<!--             @click="useGetDocumentsList(row.grid.id, route, store, shipments)"-->
<!--             class="fa-solid fa-folder-open grid-docs-icon">-->
<!--          </i>-->
        </template>
        <template v-slot:pickupDate="row">
          <GridCellDetail
              v-if="row.grid.pickupDate.est || row.grid.pickupDate.actual"
              v-model:cellColor="row.grid.pickupDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.pickupDate.est"
                  :estTime="row.grid.pickupDate.estimatedTime"
                  :actualDate="row.grid.pickupDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.pickupDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:arrivalDate="row">
          <GridCellDetail
              v-if="row.grid.arrivalDate.est || row.grid.arrivalDate.actual"
              v-model:cellColor="row.grid.arrivalDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.arrivalDate.est"
                  :estTime="row.grid.arrivalDate.estimatedTime"
                  :actualDate="row.grid.arrivalDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.arrivalDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:departureDate="row">
          <GridCellDetail
              v-if="row.grid.departureDate.est || row.grid.departureDate.actual"
              v-model:cellColor="row.grid.departureDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.departureDate.est"
                  :estTime="row.grid.departureDate.estimatedTime"
                  :actualDate="row.grid.departureDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.departureDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:deliveryDate="row">
          <GridCellDetail
              v-if="row.grid.deliveryDate.est || row.grid.deliveryDate.actual"
              v-model:cellColor="row.grid.deliveryDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.deliveryDate.est"
                  :estTime="row.grid.deliveryDate.estimatedTime"
                  :actualDate="row.grid.deliveryDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.deliveryDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:customs="row">
          <GridCellDetail
              v-if="row.grid.customs.est || row.grid.customs.actual"
              v-model:cellColor="row.grid.customs.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.customs.est"
                  :estTime="row.grid.customs.estimatedTime"
                  :actualDate="row.grid.customs.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.customs) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:cleared="row">
          <GridCellDetail
              v-if="row.grid.cleared.est || row.grid.cleared.actual"
              v-model:cellColor="row.grid.cleared.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.cleared.est"
                  :estTime="row.grid.cleared.estimatedTime"
                  :actualDate="row.grid.cleared.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.cleared) }}</template>
          </GridCellDetail>
        </template>
      </StickyGrid>
      <NoDataFoundBanner v-else>
        <div style="display: flex; align-items: center; gap:20px;">
          <img src="@/assets/icons/mode_road.png">No Data Found For Road
        </div>
      </NoDataFoundBanner>
    </TabsContent>
    <TabsContent
        v-if="store.state.userSettings.showShipmentsTabSettings.sea"
        :icon="require(`@/assets/icons/mode_sea.png`)"
        :name="shipments.modes.sea"
        :badgeData="getSeaLength"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <StickyGrid
          :key="refreshGrids.sea"
          :height="580"
          :page-size="200"
          @click-save="saveGridColumns"
          @click-reset="resetGridColumns"
          @sort-column="handleColumnSort"
          v-if="shipments.shipmentsData.sea.length > 0"
          v-model:currentPage="shipments.shipmentGridCurrentPage.sea"
          v-model:currentPaginationRange="shipments.shipmentGridCurrentPaginationPage.sea"
          :allowGridMenu="store.state.userSettings.allowColumnMenuSearchShipments"
          :allow-column-sort="true"
          :allow-column-resize="true"
          :selectable="false"
          :row-selection="false"
          :columns="store.state.userSettings.searchShipmentColumns.sea"
          :dataSource="shipments.shipmentsData.sea">
        <template #right-content>
          <GridModeDataNotification
              style="margin-left: auto">
          </GridModeDataNotification>
        </template>
        <template v-slot:selectRow="row">
          <Button
              v-if="row.grid.consol == -1"
              style="width:10px"
              title="View This Job"
              data-cy="btnViewShipment"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="V">
          </Button>
          <Button
              v-if="row.grid.consol== -1"
              style="width:10px; margin-left: 7px"
              title="Master Job / View Related Jobs"
              data-cy="btnViewShipment"
              @click="viewConsolJobs(row.grid)"
              text="M">
          </Button>
          <Button
              v-else
              data-cy="btnViewShipment"
              title="View This Job"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="View">
          </Button>
        </template>
        <template v-slot:editBooking="row">
          <Button
              :disabled="!row.grid.isEditable"
              data-cy="btnViewShipment"
              @click="getBookingType(row)"
              type="info"
              text="Edit">
          </Button>
        </template>
        <template v-slot:trackJob="row">
          <Button
              v-if="store.state.userSettings.showSeaTrackingLink"
              :disabled="!row.grid.trackingUrl"
              data-cy="btnViewShipment"
              icon="<i class='fa-duotone fa-arrow-up-right-from-square'></i>"
              @click="handleTrackingLink(row.rowData.trackingUrl)"
              type="secondary"
              text="TRACK">
          </Button>
        </template>
        <template v-slot:documents="row">
          <div v-if="row.grid.documents"
               title="View Documents"
               @click="useGetDocumentsList(row.grid.id, route, store, shipments)"
               class="fa-solid fa-folder-open grid-docs-icon">&#128194
          </div>
<!--          <i v-if="row.grid.documents"-->
<!--             title="View Documents"-->
<!--             @click="useGetDocumentsList(row.grid.id, route, store, shipments)"-->
<!--             class="fa-solid fa-folder-open grid-docs-icon">-->
<!--          </i>-->
        </template>
        <template v-slot:pickupDate="row">
          <GridCellDetail
              v-if="row.grid.pickupDate.est || row.grid.pickupDate.actual"
              v-model:cellColor="row.grid.pickupDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.pickupDate.est"
                  :estTime="row.grid.pickupDate.estimatedTime"
                  :actualDate="row.grid.pickupDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.pickupDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:arrivalDate="row">
          <GridCellDetail
              v-if="row.grid.arrivalDate.est || row.grid.arrivalDate.actual"
              v-model:cellColor="row.grid.arrivalDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.arrivalDate.est"
                  :estTime="row.grid.arrivalDate.estimatedTime"
                  :actualDate="row.grid.arrivalDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.arrivalDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:departureDate="row">
          <GridCellDetail
              v-if="row.grid.departureDate.est || row.grid.departureDate.actual"
              v-model:cellColor="row.grid.departureDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.departureDate.est"
                  :estTime="row.grid.departureDate.estimatedTime"
                  :actualDate="row.grid.departureDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.departureDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:deliveryDate="row">
          <GridCellDetail
              v-if="row.grid.deliveryDate.est || row.grid.deliveryDate.actual"
              v-model:cellColor="row.grid.deliveryDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.deliveryDate.est"
                  :estTime="row.grid.deliveryDate.estimatedTime"
                  :actualDate="row.grid.deliveryDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.deliveryDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:customs="row">
          <GridCellDetail
              v-if="row.grid.customs.est || row.grid.customs.actual"
              v-model:cellColor="row.grid.customs.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.customs.est"
                  :estTime="row.grid.customs.estimatedTime"
                  :actualDate="row.grid.customs.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.customs) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:cleared="row">
          <GridCellDetail
              v-if="row.grid.cleared.est || row.grid.cleared.actual"
              v-model:cellColor="row.grid.cleared.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.cleared.est"
                  :estTime="row.grid.cleared.estimatedTime"
                  :actualDate="row.grid.cleared.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.cleared) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:closing="row">
          <GridCellDetail
              v-if="row.grid.closing.est || row.grid.closing.actual"
              v-model:cellColor="row.grid.closing.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.closing.est"
                  :estTime="row.grid.closing.estimatedTime"
                  :actualDate="row.grid.closing.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.closing) }}</template>
          </GridCellDetail>
        </template>
      </StickyGrid>
      <NoDataFoundBanner v-else>
        <div style="display: flex; align-items: center; gap:20px;">
          <img src="@/assets/icons/mode_sea.png">No Data Found For Sea
        </div>
      </NoDataFoundBanner>
    </TabsContent>
    <TabsContent
        v-if="store.state.userSettings.showShipmentsTabSettings.rail"
        :icon="require(`@/assets/icons/mode_rail.png`)"
        :name="shipments.modes.rail"
        :badgeData="getRailLength"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <StickyGrid
          :key="refreshGrids.rail"
          :height="580"
          :page-size="200"
          @click-save="saveGridColumns"
          @click-reset="resetGridColumns"
          @sort-column="handleColumnSort"
          v-if="shipments.shipmentsData.rail.length > 0"
          v-model:currentPage="shipments.shipmentGridCurrentPage.rail"
          v-model:currentPaginationRange="shipments.shipmentGridCurrentPaginationPage.rail"
          :allowGridMenu="store.state.userSettings.allowColumnMenuSearchShipments"
          :allow-column-sort="true"
          :allow-column-resize="true"
          :selectable="false"
          :row-selection="false"
          :columns="store.state.userSettings.searchShipmentColumns.rail"
          :dataSource="shipments.shipmentsData.rail">
        <template #right-content>
          <GridModeDataNotification
              style="margin-left: auto">
          </GridModeDataNotification>
        </template>
        <template v-slot:selectRow="row">
          <Button
              v-if="row.grid.consol == -1"
              style="width:10px"
              title="View This Job"
              data-cy="btnViewShipment"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="V">
          </Button>
          <Button
              v-if="row.grid.consol== -1"
              style="width:10px; margin-left: 7px"
              title="Master Job / View Related Jobs"
              data-cy="btnViewShipment"
              @click="viewConsolJobs(row.grid)"
              text="M">
          </Button>
          <Button
              v-else
              data-cy="btnViewShipment"
              title="View This Job"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="View">
          </Button>
        </template>
        <template v-slot:editBooking="row">
          <Button
              :disabled="!row.grid.isEditable"
              data-cy="btnViewShipment"
              @click="getBookingType(row)"
              type="info"
              text="Edit">
          </Button>
        </template>
        <template v-slot:documents="row">
          <div v-if="row.grid.documents"
               title="View Documents"
               @click="useGetDocumentsList(row.grid.id, route, store, shipments)"
               class="fa-solid fa-folder-open grid-docs-icon">&#128194
          </div>
<!--          <i v-if="row.grid.documents"-->
<!--             title="View Documents"-->
<!--             @click="useGetDocumentsList(row.grid.id, route, store, shipments)"-->
<!--             class="fa-solid fa-folder-open grid-docs-icon">-->
<!--          </i>-->
        </template>
        <template v-slot:pickupDate="row">
          <GridCellDetail
              v-if="row.grid.pickupDate.est || row.grid.pickupDate.actual"
              v-model:cellColor="row.grid.pickupDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.pickupDate.est"
                  :estTime="row.grid.pickupDate.estimatedTime"
                  :actualDate="row.grid.pickupDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.pickupDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:arrivalDate="row">
          <GridCellDetail
              v-if="row.grid.arrivalDate.est || row.grid.arrivalDate.actual"
              v-model:cellColor="row.grid.arrivalDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.arrivalDate.est"
                  :estTime="row.grid.arrivalDate.estimatedTime"
                  :actualDate="row.grid.arrivalDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.arrivalDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:departureDate="row">
          <GridCellDetail
              v-if="row.grid.departureDate.est || row.grid.departureDate.actual"
              v-model:cellColor="row.grid.departureDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.departureDate.est"
                  :estTime="row.grid.departureDate.estimatedTime"
                  :actualDate="row.grid.departureDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.departureDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:deliveryDate="row">
          <GridCellDetail
              v-if="row.grid.deliveryDate.est || row.grid.deliveryDate.actual"
              v-model:cellColor="row.grid.deliveryDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.deliveryDate.est"
                  :estTime="row.grid.deliveryDate.estimatedTime"
                  :actualDate="row.grid.deliveryDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.deliveryDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:customs="row">
          <GridCellDetail
              v-if="row.grid.customs.est || row.grid.customs.actual"
              v-model:cellColor="row.grid.customs.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.customs.est"
                  :estTime="row.grid.customs.estimatedTime"
                  :actualDate="row.grid.customs.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.customs) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:cleared="row">
          <GridCellDetail
              v-if="row.grid.cleared.est || row.grid.cleared.actual"
              v-model:cellColor="row.grid.cleared.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.cleared.est"
                  :estTime="row.grid.cleared.estimatedTime"
                  :actualDate="row.grid.cleared.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.cleared) }}</template>
          </GridCellDetail>
        </template>
      </StickyGrid>
      <NoDataFoundBanner v-else>
        <div style="display: flex; align-items: center; gap:20px;">
          <img src="@/assets/icons/mode_rail.png">No Data Found For Rail
        </div>
      </NoDataFoundBanner>
    </TabsContent>
    <TabsContent
        v-if="store.state.userSettings.showShipmentsTabSettings.courier"
        :icon="require(`@/assets/icons/mode_courier.png`)"
        :name="shipments.modes.courier"
        :badgeData="getCourierLength"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <StickyGrid
          :key="refreshGrids.courier"
          :height="580"
          :page-size="200"
          @click-save="saveGridColumns"
          @click-reset="resetGridColumns"
          @sort-column="handleColumnSort"
          v-if="shipments.shipmentsData.courier.length > 0"
          v-model:currentPage="shipments.shipmentGridCurrentPage.courier"
          v-model:currentPaginationRange="shipments.shipmentGridCurrentPaginationPage.courier"
          :allowGridMenu="store.state.userSettings.allowColumnMenuSearchShipments"
          :allow-column-sort="true"
          :allow-column-resize="true"
          :selectable="false"
          :row-selection="false"
          :columns="store.state.userSettings.searchShipmentColumns.courier"
          :dataSource="shipments.shipmentsData.courier">
        <template #right-content>
          <GridModeDataNotification
              style="margin-left: auto">
          </GridModeDataNotification>
        </template>
        <template v-slot:selectRow="row">
          <Button
              v-if="row.grid.consol == -1"
              style="width:10px"
              title="View This Job"
              data-cy="btnViewShipment"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="V">
          </Button>
          <Button
              v-if="row.grid.consol== -1"
              style="width:10px; margin-left: 7px"
              title="Master Job / View Related Jobs"
              data-cy="btnViewShipment"
              @click="viewConsolJobs(row.grid)"
              text="M">
          </Button>
          <Button
              v-else
              data-cy="btnViewShipment"
              title="View This Job"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="View">
          </Button>
        </template>
        <template v-slot:editBooking="row">
          <Button
              :disabled="!row.grid.isEditable"
              data-cy="btnViewShipment"
              @click="getBookingType(row)"
              type="info"
              text="Edit">
          </Button>
        </template>
        <template v-slot:trackJob="row">
          <Button
              v-if="store.state.userSettings.showAirTrackingLink"
              :disabled="!row.grid.trackingUrl"
              data-cy="btnViewShipment"
              icon="<i class='fa-duotone fa-arrow-up-right-from-square'></i>"
              @click="handleTrackingLink(row.rowData.trackingUrl)"
              type="secondary"
              text="TRACK">
          </Button>
        </template>
        <template v-slot:documents="row">
          <div v-if="row.grid.documents"
               title="View Documents"
               @click="useGetDocumentsList(row.grid.id, route, store, shipments)"
               class="fa-solid fa-folder-open grid-docs-icon">&#128194
          </div>
<!--          <i v-if="row.grid.documents"-->
<!--             title="View Documents"-->
<!--             @click="useGetDocumentsList(row.grid.id, route, store, shipments)"-->
<!--             class="fa-solid fa-folder-open grid-docs-icon">-->
<!--          </i>-->
        </template>
        <template v-slot:pickupDate="row">
          <GridCellDetail
              v-if="row.grid.pickupDate.est || row.grid.pickupDate.actual"
              v-model:cellColor="row.grid.pickupDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.pickupDate.est"
                  :estTime="row.grid.pickupDate.estimatedTime"
                  :actualDate="row.grid.pickupDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.pickupDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:arrivalDate="row">
          <GridCellDetail
              v-if="row.grid.arrivalDate.est || row.grid.arrivalDate.actual"
              v-model:cellColor="row.grid.arrivalDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.arrivalDate.est"
                  :estTime="row.grid.arrivalDate.estimatedTime"
                  :actualDate="row.grid.arrivalDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.arrivalDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:departureDate="row">
          <GridCellDetail
              v-if="row.grid.departureDate.est || row.grid.departureDate.actual"
              v-model:cellColor="row.grid.departureDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.departureDate.est"
                  :estTime="row.grid.departureDate.estimatedTime"
                  :actualDate="row.grid.departureDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.departureDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:deliveryDate="row">
          <GridCellDetail
              v-if="row.grid.deliveryDate.est || row.grid.deliveryDate.actual"
              v-model:cellColor="row.grid.deliveryDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.deliveryDate.est"
                  :estTime="row.grid.deliveryDate.estimatedTime"
                  :actualDate="row.grid.deliveryDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.deliveryDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:customs="row">
          <GridCellDetail
              v-if="row.grid.customs.est || row.grid.customs.actual"
              v-model:cellColor="row.grid.customs.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.customs.est"
                  :estTime="row.grid.customs.estimatedTime"
                  :actualDate="row.grid.customs.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.customs) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:cleared="row">
          <GridCellDetail
              v-if="row.grid.cleared.est || row.grid.cleared.actual"
              v-model:cellColor="row.grid.cleared.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.cleared.est"
                  :estTime="row.grid.cleared.estimatedTime"
                  :actualDate="row.grid.cleared.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.cleared) }}</template>
          </GridCellDetail>
        </template>
      </StickyGrid>
      <NoDataFoundBanner v-else>
        <div style="display: flex; align-items: center; gap:20px;">
          <img src="@/assets/icons/mode_courier.png">No Data Found For Courier
        </div>
      </NoDataFoundBanner>
    </TabsContent>
    <TabsContent
        v-if="store.state.userSettings.showShipmentsTabSettings.other"
        :name="shipments.modes.other"
        :badgeData="getOtherLength"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <StickyGrid
          :key="refreshGrids.other"
          :height="580"
          :page-size="200"
          @click-save="saveGridColumns"
          @click-reset="resetGridColumns"
          @sort-column="handleColumnSort"
          v-if="shipments.shipmentsData.other.length > 0"
          v-model:currentPage="shipments.shipmentGridCurrentPage.other"
          v-model:currentPaginationRange="shipments.shipmentGridCurrentPaginationPage.other"
          :allowGridMenu="store.state.userSettings.allowColumnMenuSearchShipments"
          :allow-column-sort="true"
          :allow-column-resize="true"
          :selectable="false"
          :row-selection="false"
          :columns="store.state.userSettings.searchShipmentColumns.other"
          :dataSource="shipments.shipmentsData.other">
        <template #right-content>
          <GridModeDataNotification
              style="margin-left: auto">
          </GridModeDataNotification>
        </template>
        <template v-slot:selectRow="row">
          <Button
              v-if="row.grid.consol == -1"
              style="width:10px"
              title="View This Job"
              data-cy="btnViewShipment"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="V">
          </Button>
          <Button
              v-if="row.grid.consol== -1"
              style="width:10px; margin-left: 7px"
              title="Master Job / View Related Jobs"
              data-cy="btnViewShipment"
              @click="viewConsolJobs(row.grid)"
              text="M">
          </Button>
          <Button
              v-else
              data-cy="btnViewShipment"
              title="View This Job"
              @click="viewSelectedShipment(row.grid.id)"
              type="toolbar"
              text="View">
          </Button>
        </template>
        <template v-slot:editBooking="row">
          <Button
              :disabled="!row.grid.isEditable"
              data-cy="btnViewShipment"
              @click="getBookingType(row)"
              type="info"
              text="Edit">
          </Button>
        </template>
        <template v-slot:documents="row">
          <div v-if="row.grid.documents"
               title="View Documents"
               @click="useGetDocumentsList(row.grid.id, route, store, shipments)"
               class="fa-solid fa-folder-open grid-docs-icon">&#128194
          </div>
<!--          <i v-if="row.grid.documents"-->
<!--             title="View Documents"-->
<!--             @click="useGetDocumentsList(row.grid.id, route, store, shipments)"-->
<!--             class="fa-solid fa-folder-open grid-docs-icon">-->
<!--          </i>-->
        </template>
        <template v-slot:pickupDate="row">
          <GridCellDetail
              v-if="row.grid.pickupDate.est || row.grid.pickupDate.actual"
              v-model:cellColor="row.grid.pickupDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.pickupDate.est"
                  :estTime="row.grid.pickupDate.estimatedTime"
                  :actualDate="row.grid.pickupDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.pickupDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:arrivalDate="row">
          <GridCellDetail
              v-if="row.grid.arrivalDate.est || row.grid.arrivalDate.actual"
              v-model:cellColor="row.grid.arrivalDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.arrivalDate.est"
                  :estTime="row.grid.arrivalDate.estimatedTime"
                  :actualDate="row.grid.arrivalDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.arrivalDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:departureDate="row">
          <GridCellDetail
              v-if="row.grid.departureDate.est || row.grid.departureDate.actual"
              v-model:cellColor="row.grid.departureDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.departureDate.est"
                  :estTime="row.grid.departureDate.estimatedTime"
                  :actualDate="row.grid.departureDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.departureDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:deliveryDate="row">
          <GridCellDetail
              v-if="row.grid.deliveryDate.est || row.grid.deliveryDate.actual"
              v-model:cellColor="row.grid.deliveryDate.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.deliveryDate.est"
                  :estTime="row.grid.deliveryDate.estimatedTime"
                  :actualDate="row.grid.deliveryDate.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.deliveryDate) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:customs="row">
          <GridCellDetail
              v-if="row.grid.customs.est || row.grid.customs.actual"
              v-model:cellColor="row.grid.customs.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.customs.est"
                  :estTime="row.grid.customs.estimatedTime"
                  :actualDate="row.grid.customs.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.customs) }}</template>
          </GridCellDetail>
        </template>
        <template v-slot:cleared="row">
          <GridCellDetail
              v-if="row.grid.cleared.est || row.grid.cleared.actual"
              v-model:cellColor="row.grid.cleared.status">
            <template #details>
              <DateInformationTooltip
                  :estDate="row.grid.cleared.est"
                  :estTime="row.grid.cleared.estimatedTime"
                  :actualDate="row.grid.cleared.actual">
              </DateInformationTooltip>
            </template>
            <template #cell-data>{{ getCorrectDate(row.grid.cleared) }}</template>
          </GridCellDetail>
        </template>
      </StickyGrid>
      <NoDataFoundBanner v-else>
        <div style="display: flex; align-items: center; gap:20px;">
          No Data Found For Other
        </div>
      </NoDataFoundBanner>
    </TabsContent>
    <TabsContent
        style="width:40px; padding:0; opacity: 55%"
        :name="shipments.modes.select"
        :selectedTab="shipments.selectedShowShipmentsTab">
      <div class="cmp-shipments_tab-menu">
        <i class="fa-sharp fa-thin fa-gears cmp-shipments_tab-menu-cogs-icon"></i>
        <div style="margin-bottom: 20px; font-weight: bold">Add or remove current tabs
<!--          <span style="font-size: 10px; font-weight: normal; color: #8f2c2c ">(slight delay will occur when selecting boxes due to data reloading.)</span>-->
          <div style="font-size: 12px; font-weight: normal;">
            <i class="fa-solid fa-triangle-exclamation icon-info"/>You cannot remove a tab if it's set to be default!
          </div>
          <div style="font-size: 12px; font-weight: normal;">
            <i class="fa-solid fa-triangle-exclamation icon-info"/>Default can only be set on active tabs!
          </div>
          <div style="font-size: 11px; font-weight: normal; margin-top: 20px; margin-left:140px; display: flex">
            <div>Active</div>
            <div style="margin-left: 10px">Default</div>
          </div>
        </div>
        <div style="display: flex">
          <div>
            <div v-for="tab in shipments.activeShipmentTabs">
              <CheckBox
                  @change="handleTabSelection(tab.name)"
                  v-model="tab.active"
                  :label="tab.name">
              </CheckBox>
            </div>
          </div>
          <div>
            <div class="cmp-shipments_tab-menu-radio-buttons">
              <div class="cmp-shipments_tab-menu-radio-input">
                <input type="radio" name="tabs" id="Air" value="Air" @change="handleRadioButton('Air')"
                       v-model="store.state.userSettings.showShipmentsTabSettings.default"/>
                <span v-if="shipments.currentDefaultTab === 'Air'">(current)</span>
              </div>
              <div class="cmp-shipments_tab-menu-radio-input">
                <input type="radio" name="tabs" id="Road" value="Road" @change="handleRadioButton('Road')"
                       v-model="store.state.userSettings.showShipmentsTabSettings.default"/>
                <span v-if="shipments.currentDefaultTab === 'Road'">(current)</span>
              </div>
              <div class="cmp-shipments_tab-menu-radio-input">
                <input type="radio" name="tabs" id="Sea" value="Sea" @change="handleRadioButton('Sea')"
                       v-model="store.state.userSettings.showShipmentsTabSettings.default"/>
                <span v-if="shipments.currentDefaultTab === 'Sea'">(current)</span>
              </div>
              <div class="cmp-shipments_tab-menu-radio-input">
                <input type="radio" name="tabs" id="Rail" value="Rail" @change="handleRadioButton('Rail')"
                       v-model="store.state.userSettings.showShipmentsTabSettings.default"/>
                <span v-if="shipments.currentDefaultTab === 'Rail'">(current)</span>
              </div>
              <div class="cmp-shipments_tab-menu-radio-input">
                <input type="radio" name="tabs" id="Courier" value="Courier" @change="handleRadioButton('Courier')"
                       v-model="store.state.userSettings.showShipmentsTabSettings.default"/>
                <span v-if="shipments.currentDefaultTab === 'Courier'">(current)</span>
              </div>
              <div class="cmp-shipments_tab-menu-radio-input">
                <input type="radio" name="tabs" id="Other" value="Other" @change="handleRadioButton('Other')"
                       v-model="store.state.userSettings.showShipmentsTabSettings.default"/>
                <span v-if="shipments.currentDefaultTab === 'Other'">(current)</span>
              </div>
            </div>
          </div>
        </div>
        <div style="margin-top: 20px;">
          <Button
              title="Save tab configuration. New default tab will be active after refresh of the browser"
              style="margin-left: 0"
              @click="handleTabConfigSave()"
              text="Save">
          </Button>
        </div>
      </div>
    </TabsContent>
  </Tabs>
</template>

<style lang="scss" scoped>

.cmp-shipments {

  &_tab-menu {
    height: 310px;
    border: 1px solid var(--border-color);
    padding: 20px 0 20px 40px;
    border-radius: var(--rounded-corners);
    border-left: 6px solid var(--left-border-highlight);
    background-color: #fdfdfd;
  }

  &_tab-menu-cogs-icon {
    position:absolute;
    right: 420px;
    font-size: 320px;
    opacity: 2%;

  }

  &_tab-menu-radio-buttons {
    margin-top: 2px;
    margin-left: 40px;
    display: flex;
    flex-direction: column;
  }

  &_tab-menu-radio-input {
    font-size: 11px;
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    color: #566ea0;
  }

  &_tab-menu-radio-input > span {
    margin-left: 6px;
  }

  &_toolbar {
    display: flex;
  }

  &_actual-date-color {
    height: 14px;
    width: 34px;
    border-radius: 3px;
    margin-right: 5px;
    background: var(--actual-date);
  }

  &_search-icon {
    font-size: 24px;
    color: #114362;
    margin: 0 60px 0 10px;
  }

  &_search-icon:hover {
    color: #2f9260;
  }

  &_filter-section {
    display: flex;
    align-items: center;
    height: 20px;
    padding: 5px 0 5px 40px;
    border-radius: 10px;
    background: #fffffa;
    border-bottom: 1px solid var(--border-color);
    box-shadow: var(--box-shadow);
  }

  &_filter-section > label {
    margin-right: 20px;
    color: #101010;
    font-size: 13px;
  }

  &_filter-section > input {
    margin: 0 5px 0 0;
  }

  &_data-search-message {
    font-size: 14px;
    width: 300px;
  }

  &_hide-completed-chkbox {
    margin-left: 20px;
    height: 22px;
  }

  &_filter-by-text {
    margin-right: 12px;
    font-size: 12px;
  }

  &_hide-completed {
    width: 160px;
  }

  &_refresh-grid {
    position: absolute;
    top: 113px;
    left: 650px;
    display: flex;
    gap: 20px;
  }

  @media screen and (max-width: 1220px) {
    &_tab-menu-cogs-icon {
      display: none;
    }

  }

  @media screen and (max-width: 1530px) {
    &_data-search-message {
      display: none;
    }

  }

}

@media screen and (max-width: 1820px) {
  .excel-download-message {
    display: none;
  }

}


</style>
